import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
import { isPvpMenuTarget } from './pvpTarget';

/**
 * The Dice Wager (ADR-0154): "a duel resolved by dice instead of combat", built by
 * reuse - the duel's wager prompt (dice copy via `matchNoun`), the shared blue
 * target cursor (armed for the `'dice'` purpose), and the same PvP menu-row gate as
 * the duel row. The dice-and-cup keeps its double-click solo roll (ADR-0083); a
 * long-press on the cup is the wager affordance.
 */
export function installDice(ctx: FeatureContext): void {
  // Open the dice-wager challenge flow for an already-known target: reuse the duel
  // wager prompt (with dice copy) to set the stake, then send a `DiceWagerChallenge`
  // up the socket. Shared by the two entry points - the long-pressed player's
  // "Roll the dice" menu item (target known immediately) and the dice-and-cup's
  // target-cursor pick (target chosen via the blue cursor). Anonymous sessions are
  // capped at a zero-wager honor roll, exactly like an honor duel. The server rules
  // on the target (self / rat / NPC / ghost / range / busy across both match
  // systems) and the wager; an invalid pick fizzles silently.
  const startDiceWager = (target: EntityId): void => {
    if (ctx.playerId() === undefined) return;
    const opponentName = ctx.world()?.get(target)?.renderable?.name ?? 'that player';
    const gold = ctx.state.gold();
    ctx.state.duelUi.promptWager({
      opponentName,
      matchNoun: 'a dice wager',
      maxGold: gold.durable ? gold.amount : 0,
      canStake: gold.durable,
      onConfirm: (wager) => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) {
          ctx.client()?.sendCommand({
            kind: 'DiceWagerChallenge',
            entity: playerId,
            opponent: target,
            wager,
          });
        }
      },
    });
  };

  // A dice-wager cursor pick (armed from the dice-and-cup's "Roll the dice" menu)
  // opens the dice-wager prompt for the picked target.
  ctx.state.onTargetPick('dice', startDiceWager);

  ctx.onRendererBuilt((renderer) => {
    // Double-clicking the dice-and-cup sends a RollDice up the socket - intent only.
    // The server rolls 2d6 (never the client), enforces the ~10s per-player cooldown,
    // and broadcasts the `*Name rolls d1,d2*` emote back over the dice (ADR-0083).
    renderer.onDiceUse = (dice) => {
      const playerId = ctx.playerId();
      if (playerId !== undefined) {
        ctx.client()?.sendCommand({ kind: 'RollDice', entity: playerId, dice });
      }
    };
  });

  // The dice-and-cup's long-press menu: "Roll the dice" arms the dice-wager target
  // cursor (then pick a player to challenge). A menu *surface*, so a cup is
  // hit-tested before the character pool and its menu wins the hit.
  ctx.menu.addSurface((req) => {
    const diceId = ctx.renderer()?.pickDice(req.cx, req.cy);
    if (diceId === undefined) return null;
    return [{ label: 'Roll the dice', onSelect: () => ctx.state.armTargeting('dice') }];
  });

  // The long-pressed player's "Roll the dice" row (target known immediately),
  // gated by the shared PvP row filter. Registered before the duel installer's
  // "Duel <name>" row, preserving the row order.
  ctx.menu.addEntityMenu((target) => {
    if (!isPvpMenuTarget(ctx.world(), ctx.playerId(), target.id, target.name)) return null;
    return { items: [{ label: 'Roll the dice', onSelect: () => startDiceWager(target.id) }] };
  });

  // The owner-only dice-wager challenge prompt: the dice twin of the duel prompt -
  // the same accept/decline window with dice copy (`matchNoun`). Accept means
  // matching the wager; both send the matching command up the socket. The server
  // escrows both stakes, rolls, and narrates every beat via the herald.
  ctx.events.on('diceWagerPrompt', (msg) => {
    if (!msg.open) {
      ctx.state.duelUi.dismissChallenge();
      return;
    }
    ctx.state.duelUi.showChallenge({
      challengerName: msg.challengerName,
      wager: msg.wager,
      matchNoun: 'a dice wager',
      onAccept: () => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) {
          ctx.client()?.sendCommand({ kind: 'DiceWagerAccept', entity: playerId });
        }
      },
      onDecline: () => {
        const playerId = ctx.playerId();
        if (playerId !== undefined) {
          ctx.client()?.sendCommand({ kind: 'DiceWagerDecline', entity: playerId });
        }
      },
    });
  });
}
